/**
 * Utility to generate a device fingerprint for Sentlo users.
 * Captures browser, OS, and unique characteristics for security audits.
 */

export interface DeviceInfo {
  id: string;
  name: string;
  type: 'mobile' | 'desktop' | 'tablet';
  browser: string;
  os: string;
  lastUsed: string;
  location?: string;
}

export function getDeviceFingerprint(): DeviceInfo {
  const ua = navigator.userAgent;
  let browser = "Unknown Browser";
  let os = "Unknown OS";
  let type: 'mobile' | 'desktop' | 'tablet' = 'desktop';

  // Basic Browser Detection
  if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Chrome")) browser = "Chrome";
  else if (ua.includes("Safari")) browser = "Safari";
  else if (ua.includes("Edge")) browser = "Edge";

  // Basic OS Detection
  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac OS")) os = "macOS";
  else if (ua.includes("Android")) { os = "Android"; type = 'mobile'; }
  else if (ua.includes("iPhone")) { os = "iOS"; type = 'mobile'; }
  else if (ua.includes("iPad")) { os = "iPadOS"; type = 'tablet'; }

  // Create a pseudo-unique ID based on characteristics (not perfect but works for local sessions)
  const screenRes = `${window.screen.width}x${window.screen.height}`;
  const id = btoa(`${ua}-${screenRes}`).substring(0, 12);

  return {
    id,
    name: `${browser} on ${os}`,
    type,
    browser,
    os,
    lastUsed: new Date().toISOString()
  };
}

/**
 * Persists the current device into the user's history in localStorage
 * Returns whether this was a newly recognized device.
 */
export function recordDeviceLogin(userId: string): { devices: DeviceInfo[], isNewDevice: boolean } {
  const fingerprint = getDeviceFingerprint();
  const storageKey = `sentlo_devices_${userId}`;
  
  const existingDevicesRaw = localStorage.getItem(storageKey);
  let devices: DeviceInfo[] = existingDevicesRaw ? JSON.parse(existingDevicesRaw) : [];
  
  const existingIndex = devices.findIndex(d => d.id === fingerprint.id);
  const isNewDevice = existingIndex === -1;
  
  if (!isNewDevice) {
    devices[existingIndex] = { ...devices[existingIndex], lastUsed: fingerprint.lastUsed };
  } else {
    devices.unshift(fingerprint);
  }

  // Keep only the last 5 devices for performance
  localStorage.setItem(storageKey, JSON.stringify(devices.slice(0, 5)));
  return { devices, isNewDevice };
}

export function getSavedDevices(userId: string): DeviceInfo[] {
  const storageKey = `sentlo_devices_${userId}`;
  const existingDevicesRaw = localStorage.getItem(storageKey);
  return existingDevicesRaw ? JSON.parse(existingDevicesRaw) : [];
}
